export function dipToDipDirection(dip, dipDirection) {
  return dipDirection;
}

export function formatStrike(strike) {
  if (strike == null) return '';
  const normalized = ((strike % 360) + 360) % 360;
  const quadrant = getQuadrant(normalized);
  const azimuth = normalized.toFixed(1).replace(/\.0$/, '');
  return `${azimuth}° ${quadrant}`;
}

function getQuadrant(azimuth) {
  if (azimuth >= 315 || azimuth < 45) return 'N';
  if (azimuth < 135) return 'E';
  if (azimuth < 225) return 'S';
  return 'W';
}

export function dipToDipFormat(dip) {
  if (dip == null) return '';
  const sign = dip >= 0 ? '+' : '';
  return `${sign}${dip.toFixed(1)}°`;
}

export function buildSurveyStationIndex(surveyRows) {
  const byHole = new Map();
  if (!surveyRows?.length) return byHole;
  for (const row of surveyRows) {
    const holeId = (row?.hole_id || row?.holeId || '').toString().trim().toLowerCase();
    if (!holeId) continue;
    if (!byHole.has(holeId)) byHole.set(holeId, []);
    byHole.get(holeId).push(row);
  }
  return byHole;
}

export function resolveDipAzimuthRows(rows, stationIndex) {
  if (!rows?.length) return { rows: [], unresolved: [] };
  const resolved = [];
  const unresolved = [];
  for (const row of rows) {
    if (row == null) continue;
    const alpha = Number(row?.alpha);
    const beta = Number(row?.beta);
    const dip = Number(row?.dip);
    const azimuth = Number(row?.azimuth);
    if (Number.isFinite(dip) && Number.isFinite(azimuth)) {
      resolved.push(row);
      continue;
    }
    if (!Number.isFinite(alpha) || !Number.isFinite(beta) || !stationIndex?.size) {
      unresolved.push(row);
      continue;
    }
    const holeId = (row?.hole_id || row?.holeId || '').toString().trim().toLowerCase();
    const stations = stationIndex.get(holeId);
    if (!stations?.length) {
      unresolved.push(row);
      continue;
    }
    const depth = Number(row?.depth ?? row?.md ?? 0);
    const closest = findClosestStation(stations, depth);
    if (!closest) {
      unresolved.push(row);
      continue;
    }
    const stationDip = Number(closest?.dip);
    const stationAzimuth = Number(closest?.azimuth);
    if (!Number.isFinite(stationDip) || !Number.isFinite(stationAzimuth)) {
      unresolved.push(row);
      continue;
    }
    const cosDip = Math.cos(stationDip * Math.PI / 180);
    const sinDip = Math.sin(stationDip * Math.PI / 180);
    const cosAz = Math.cos(stationAzimuth * Math.PI / 180);
    const sinAz = Math.sin(stationAzimuth * Math.PI / 180);
    const cosAlpha = Math.cos(alpha * Math.PI / 180);
    const sinAlpha = Math.sin(alpha * Math.PI / 180);
    const cosBeta = Math.cos(beta * Math.PI / 180);
    const sinBeta = Math.sin(beta * Math.PI / 180);
    const derivedDip = Math.asin(Math.max(-1, Math.min(1, -(-sinAlpha * sinDip + cosAlpha * cosDip)))) * 180 / Math.PI;
    const derivedAzimuth = (Math.atan2(
      sinAlpha * cosBeta * cosDip * cosAz - sinAlpha * sinBeta * sinAz + cosAlpha * sinDip * cosAz,
      sinAlpha * cosBeta * cosDip * sinAz + sinAlpha * sinBeta * cosAz + cosAlpha * sinDip * sinAz
    ) * 180 / Math.PI + 360) % 360;
    resolved.push({ ...row, dip: derivedDip, azimuth: derivedAzimuth, orientation_source: 'alpha_beta_resolved' });
  }
  return { rows: resolved, unresolved };
}

function findClosestStation(stations, depth) {
  if (!stations?.length) return null;
  let best = null;
  let bestDist = Infinity;
  for (const station of stations) {
    const stationDepth = Number(station?.depth ?? station?.md ?? 0);
    const dist = Math.abs(stationDepth - depth);
    if (dist < bestDist) {
      bestDist = dist;
      best = station;
    }
  }
  return best;
}
